import {
	Button,
	ButtonGroup,
	Card,
	Checkbox,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
} from "@overmap-ai/blocks"
import {
	type Procedure,
	type ProcedureStep,
	type ProcedureStepFieldValues,
	ProcedureStepFieldValuesReviewType,
	type ProcedureStepFields,
	type Stored,
	toUuidArray,
	type User,
	EMPTY_ARRAY,
} from "@overmap-ai/common"
import {
	selectAttachmentsOfProcedureStepFieldValues,
	selectLatestProcedureStepFieldsOfProcedureStep,
	selectProcedureStepFieldsById,
	selectProcedureStepFieldsOfProcedureStep,
	selectLatestReviewOfProcedureStepFieldValues,
	selectProcedureStepAssigneesOfProcedureAndProcedureStep,
	selectProcedureStepCommentsOfProcedureAndProcedureStep,
	selectProcedureStepEventsOfProcedureAndProcedureStep,
	selectProcedureStepFieldValuesOfProcedureAndProcedureStep,
	selectProcedureStepFieldValuesReviewsOfProcedureAndProcedureStep,
	selectProcedureStepReviewersOfProcedureAndProcedureStep,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { ConditionManager, type FieldValues } from "@overmap-ai/forms"
import { useSyncedState } from "@overmap-ai/hooks"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { type FormEventHandler, memo, useCallback, useMemo, useState } from "react"

import { ProcedureStepCompletionStatus } from "../../enums/procedures"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS } from "../../tiptap"
import { TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatCompactDate } from "../../utils/date"
import { getProcedureStepCompletionStatus } from "../../utils/procedures"
import { ProcedureStepReviewTypeBadge } from "../Badges/ProcedureStepReviewTypeBadge"
import { UserMultiSelect } from "../DropdownMultiSelects/UserMultiSelect"
import { FieldsRevisionDropdownSelect } from "../DropdownSelects/FieldsRevisionDropdownSelect"
import {
	useDeserializeFieldSchema,
	useDeserializeFieldValues,
	useFieldValuesChangeDebounced,
	useHydrateFieldValuesWithAttachments,
} from "../Form/hooks"
import { ProcedureStepFieldsSubmitter } from "../Form/ProcedureStepFieldsSubmitter"
import {
	getAttachmentPayloadsFromFilesAndPromises,
	getFieldFilesAndPromisesToCreate,
	getFieldValuesAttachmentsToDelete,
	getIsFieldValuesDraft,
} from "../Form/utils"
import { ProcedureStepIcon } from "../Icons/ProcedureStepIcon"
import { UserIcon } from "../Icons/UserIcon"
import { ProcedureStepActivityTimeline } from "../ProcedureStepActivityTimeline/ProcedureStepActivityTimeline"
import { ProcedureStepFieldValuesReviewTypeRadioGroup } from "../RadioGroups/ProcedureStepFieldValuesReviewTypeRadioGroup"

interface ProcedureStepItemProps {
	procedure: Stored<Procedure>
	procedureStep: Stored<ProcedureStep>
	readonly?: boolean
}

export const ProcedureStepItem = memo<ProcedureStepItemProps>((props) => {
	const { procedure, procedureStep, readonly = false } = props

	const latestProcedureStepFields = useAppSelector(selectLatestProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const procedureStepReviewers = useShallowEqaulSelector(
		selectProcedureStepReviewersOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)
	const procedureStepAssignees = useShallowEqaulSelector(
		selectProcedureStepAssigneesOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)
	const procedureStepComments = useShallowEqaulSelector(
		selectProcedureStepCommentsOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)
	const procedureStepFieldValues = useShallowEqaulSelector(
		selectProcedureStepFieldValuesOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)
	const procedureStepEvents = useShallowEqaulSelector(
		selectProcedureStepEventsOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)
	const procedureStepFieldValuesReviews = useShallowEqaulSelector(
		selectProcedureStepFieldValuesReviewsOfProcedureAndProcedureStep(procedure.uuid, procedureStep.uuid),
	)

	const latestProcedureStepFieldValues = useMemo(() => {
		let ret: Stored<ProcedureStepFieldValues> | undefined = undefined

		for (const fieldValues of procedureStepFieldValues) {
			if (!ret || fieldValues.submitted_at > ret.submitted_at) {
				ret = fieldValues
			}
		}

		return ret
	}, [procedureStepFieldValues])

	const users = useShallowEqaulSelector(selectUsersOfProject(procedure.project))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const [expanded, setExpanded] = useState(false)
	const procedureStepFields = useShallowEqaulSelector(selectProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const currentProcedureStepFields = useAppSelector(
		selectProcedureStepFieldsById(latestProcedureStepFieldValues?.fields_revision ?? ""),
	)
	const [computedProcedureStepFields, setComputedProcedureStepFields] = useSyncedState(
		currentProcedureStepFields ?? latestProcedureStepFields,
	)

	const fieldSchema = useDeserializeFieldSchema(computedProcedureStepFields?.fields ?? EMPTY_ARRAY)

	const conditionManager = useMemo(
		() =>
			new ConditionManager(
				fieldSchema.deserializeConditions(computedProcedureStepFields?.field_conditions ?? []),
			),
		[fieldSchema, computedProcedureStepFields?.field_conditions],
	)

	const latestProcedureStepFieldValuesAttachments = useShallowEqaulSelector(
		selectAttachmentsOfProcedureStepFieldValues(latestProcedureStepFieldValues?.uuid ?? ""),
	)
	const latestProcedureStepFieldValuesReview = useShallowEqaulSelector(
		selectLatestReviewOfProcedureStepFieldValues(latestProcedureStepFieldValues?.uuid ?? ""),
	)

	const refreshActivity = useCallback(() => {
		return sdk.procedureStepEvents.refreshForProcedure(procedure.uuid)
	}, [procedure.uuid])

	const assignees = useMemo(() => {
		const assigneeUserIds = new Set(procedureStepAssignees.map(({ user }) => user))
		return users.filter(({ id }) => assigneeUserIds.has(id))
	}, [procedureStepAssignees, users])

	const reviewers = useMemo(() => {
		const reviewerUserIds = new Set(procedureStepReviewers.map(({ user }) => user))
		return users.filter(({ id }) => reviewerUserIds.has(id))
	}, [procedureStepReviewers, users])

	const handleAssigneesChange = useCallback(
		(users: User[]) => {
			const userIds = new Set(users.map(({ id }) => id))
			const assigneeUserIds = new Set(procedureStepAssignees.map(({ user }) => user))

			const usersToCreateFor = users.filter(({ id }) => !assigneeUserIds.has(id))
			const procedureStepAssigneesToDelete = procedureStepAssignees.filter(({ user }) => !userIds.has(user))

			const [_, bulkAddPromise] = sdk.procedureStepAssignees.bulkAdd(
				usersToCreateFor.map((user) => ({
					user: user.id,
					procedure_step: procedureStep.uuid,
					procedure: procedure.uuid,
				})),
			)

			const bulkDeletePromise = sdk.procedureStepAssignees.bulkDelete(
				procedureStepAssigneesToDelete.map(({ uuid }) => uuid),
			)

			void Promise.allSettled([bulkAddPromise, bulkDeletePromise]).finally(() => {
				void refreshActivity()
			})
		},
		[procedure.uuid, procedureStep.uuid, procedureStepAssignees, refreshActivity],
	)

	const handleReviewersChange = useCallback(
		(users: User[]) => {
			const userIds = new Set(users.map(({ id }) => id))
			const reviewerUserIds = new Set(procedureStepReviewers.map(({ user }) => user))

			const usersToCreateFor = users.filter(({ id }) => !reviewerUserIds.has(id))
			const procedureStepReviewersToDelete = procedureStepReviewers.filter(({ user }) => !userIds.has(user))

			const [_, bulkAddPromise] = sdk.procedureStepReviewers.bulkAdd(
				usersToCreateFor.map((user) => ({
					user: user.id,
					procedure_step: procedureStep.uuid,
					procedure: procedure.uuid,
				})),
			)

			const bulkDeletePromise = sdk.procedureStepReviewers.bulkDelete(
				procedureStepReviewersToDelete.map(({ uuid }) => uuid),
			)

			void Promise.allSettled([bulkAddPromise, bulkDeletePromise]).finally(() => {
				void refreshActivity()
			})
		},
		[procedure.uuid, procedureStep.uuid, procedureStepReviewers, refreshActivity],
	)

	const [showCommentSubmitter, setShowCommentSubmitter] = useState(false)
	const [showReviewSubmitter, setShowReviewSubmitter] = useState(false)
	const [procedureStepFieldValuesReviewType, setProcedureStepFieldValuesReviewType] =
		useState<ProcedureStepFieldValuesReviewType>(ProcedureStepFieldValuesReviewType.APPROVED)

	const commentSubmitterEditor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			Placeholder.configure({
				placeholder: "Leave a comment",
			}),
		],
	})

	const reviewSubmitterEditor = useEditor({
		editorProps: {
			attributes: { class: "outline-none" },
		},
		extensions: [
			...BASE_EXTENSIONS,
			...LINK_NODES,
			...MENTION_EXTENSIONS,
			Placeholder.configure({
				placeholder: "Leave a review",
			}),
		],
	})

	const handleCommentClick = useCallback(() => {
		setShowCommentSubmitter((prev) => !prev)
		setShowReviewSubmitter(false)
	}, [])

	const handleCommentCancel = useCallback(() => {
		commentSubmitterEditor?.commands.clearContent()
		setShowCommentSubmitter(false)
	}, [commentSubmitterEditor])

	const handleCommentSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!commentSubmitterEditor || commentSubmitterEditor.isEmpty) return

			const [_offlineComment, _promise] = sdk.procedureStepComments.add({
				content: JSON.stringify(commentSubmitterEditor.getJSON()),
				procedure: procedure.uuid,
				procedure_step: procedureStep.uuid,
				resolved: false,
			})

			commentSubmitterEditor.commands.clearContent()
			setShowCommentSubmitter(false)
		},
		[commentSubmitterEditor, procedure.uuid, procedureStep.uuid],
	)

	const handleReviewClick = useCallback(() => {
		setShowReviewSubmitter((prev) => !prev)
		setShowCommentSubmitter(false)
	}, [])

	const handleReviewCancel = useCallback(() => {
		reviewSubmitterEditor?.commands.clearContent()
		setProcedureStepFieldValuesReviewType(ProcedureStepFieldValuesReviewType.APPROVED)
		setShowReviewSubmitter(false)
	}, [reviewSubmitterEditor])

	const handleReviewSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()

			if (!reviewSubmitterEditor || !latestProcedureStepFieldValues?.uuid) return

			const content = reviewSubmitterEditor.isEmpty ? "" : JSON.stringify(reviewSubmitterEditor.getJSON())

			const [_offlineReview, _promise] = sdk.procedureStepFieldValuesReviews.add({
				type: procedureStepFieldValuesReviewType,
				content,
				field_values: latestProcedureStepFieldValues.uuid,
			})

			reviewSubmitterEditor.commands.clearContent()
			setProcedureStepFieldValuesReviewType(ProcedureStepFieldValuesReviewType.APPROVED)
			setShowReviewSubmitter(false)
		},
		[latestProcedureStepFieldValues?.uuid, procedureStepFieldValuesReviewType, reviewSubmitterEditor],
	)

	const isProcedureStepFieldValuesDraft = getIsFieldValuesDraft(latestProcedureStepFieldValues)

	const handleProcedureStepFieldValuesChange = useFieldValuesChangeDebounced(
		(allValues: FieldValues) => {
			if (!computedProcedureStepFields?.uuid) return

			if (!isProcedureStepFieldValuesDraft || !latestProcedureStepFieldValues?.uuid) {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.procedureStepFieldValues.add({
					fields_revision: computedProcedureStepFields.uuid,
					procedure_step: procedureStep.uuid,
					procedure: procedure.uuid,
					values: fieldSchema.serializeValues(values),
					published_at: null,
				})

				if (Object.keys(filesAndPromises).length !== 0) {
					void sdk.procedureStepFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromises,
							latestProcedureStepFieldValuesAttachments,
						),
					)
				}
			} else {
				const [values, filesAndPromises] = fieldSchema.extractFilesAndPromisesFromFieldValues(allValues)

				const [offlineFieldValues, _promise] = sdk.procedureStepFieldValues.update({
					uuid: latestProcedureStepFieldValues.uuid,
					fields_revision: computedProcedureStepFields.uuid,
					values: fieldSchema.serializeValues(values),
				})

				const attachmentsToDelete = getFieldValuesAttachmentsToDelete(
					latestProcedureStepFieldValuesAttachments,
					filesAndPromises,
				)

				const filesAndPromisesToCreate = getFieldFilesAndPromisesToCreate(
					latestProcedureStepFieldValuesAttachments,
					filesAndPromises,
				)

				if (attachmentsToDelete.length !== 0) {
					void sdk.procedureStepFieldValuesAttachments.bulkDelete(toUuidArray(attachmentsToDelete))
				}

				if (Object.keys(filesAndPromisesToCreate).length !== 0) {
					void sdk.procedureStepFieldValuesAttachments.bulkAdd(
						getAttachmentPayloadsFromFilesAndPromises(
							offlineFieldValues.uuid,
							filesAndPromisesToCreate,
							latestProcedureStepFieldValuesAttachments,
						),
					)
				}
			}
		},
		[
			fieldSchema,
			isProcedureStepFieldValuesDraft,
			latestProcedureStepFieldValues?.uuid,
			latestProcedureStepFieldValuesAttachments,
			computedProcedureStepFields?.uuid,
			procedure.uuid,
			procedureStep.uuid,
		],
	)

	const handleProcedureStepFieldsSubmit = useCallback(
		(_values: FieldValues) => {
			if (!latestProcedureStepFieldValues?.uuid) return

			handleProcedureStepFieldValuesChange.flush()

			const [_offlineProcedureStepFieldValues, _promise] = sdk.procedureStepFieldValues.update({
				uuid: latestProcedureStepFieldValues.uuid,
				published_at: new Date().toISOString(),
			})
		},
		[latestProcedureStepFieldValues?.uuid, handleProcedureStepFieldValuesChange],
	)

	const handleDeleteProcedureStepFieldValues = useCallback(() => {
		if (!latestProcedureStepFieldValues?.uuid || !isProcedureStepFieldValuesDraft) return

		openConfirmDeleteDialog({
			title: "Delete draft",
			description: "Are you sure you want to delete this draft? This action cannot be undone.",
			onAction: () => {
				handleProcedureStepFieldValuesChange.flush()
				void sdk.procedureStepFieldValues.delete(latestProcedureStepFieldValues.uuid)
			},
		})
	}, [
		isProcedureStepFieldValuesDraft,
		latestProcedureStepFieldValues?.uuid,
		openConfirmDeleteDialog,
		handleProcedureStepFieldValuesChange,
	])

	const handleProcedureStepFieldsSelect = useCallback(
		(fields: Stored<ProcedureStepFields>) => {
			handleProcedureStepFieldValuesChange.flush()
			setComputedProcedureStepFields(fields)
		},
		[handleProcedureStepFieldValuesChange, setComputedProcedureStepFields],
	)

	const fieldValues = useDeserializeFieldValues(fieldSchema, latestProcedureStepFieldValues?.values)

	const fieldValuesWithAttachments = useHydrateFieldValuesWithAttachments(
		fieldSchema,
		fieldValues,
		latestProcedureStepFieldValuesAttachments,
		sdk.procedureStepFieldValuesAttachments,
	)

	const completionStatus = useMemo(() => {
		return getProcedureStepCompletionStatus(
			fieldSchema,
			conditionManager,
			latestProcedureStepFieldValues?.values,
			!latestProcedureStepFieldValues?.published_at,
		)
	}, [fieldSchema, conditionManager, latestProcedureStepFieldValues])

	const hasActivity =
		procedureStepFieldValues.length > 0 ||
		procedureStepComments.length > 0 ||
		procedureStepEvents.length > 0 ||
		procedureStepFieldValuesReviews.length > 0

	const getCheckboxUI = () => {
		switch (completionStatus) {
			case ProcedureStepCompletionStatus.FAILED:
				return (
					<Checkbox.Root checked accentColor="danger" size="sm">
						<Checkbox.Indicator>
							<LuIcon icon={modelIconMapping.close} />
						</Checkbox.Indicator>
					</Checkbox.Root>
				)
			case ProcedureStepCompletionStatus.COMPLETE:
				return (
					<Checkbox.Root checked={true} size="sm">
						<Checkbox.Indicator>
							<LuIcon icon={modelIconMapping.check} />
						</Checkbox.Indicator>
					</Checkbox.Root>
				)
			case ProcedureStepCompletionStatus.INCOMPLETE:
				return (
					<Checkbox.Root checked={false} size="sm">
						<Checkbox.Indicator>
							<LuIcon icon={modelIconMapping.check} />
						</Checkbox.Indicator>
					</Checkbox.Root>
				)
		}
	}

	return (
		<RadixCollapsible.Root open={expanded} onOpenChange={setExpanded} asChild>
			<div role="listitem" className="flex flex-col gap-4">
				<RadixCollapsible.Trigger asChild>
					<div className="flex cursor-pointer flex-col gap-2">
						<div className="flex items-center justify-between gap-2">
							<div className="flex min-w-0 items-center gap-2">
								{getCheckboxUI()}
								<ProcedureStepIcon procedureStepId={procedureStep.uuid} />
								<Text size="sm" className="truncate">
									{procedureStep.name}
								</Text>
								{latestProcedureStepFieldValuesReview ? (
									<ProcedureStepReviewTypeBadge
										size="xs"
										type={latestProcedureStepFieldValuesReview.type}
									/>
								) : null}
							</div>

							<div className="flex items-center">
								{latestProcedureStepFieldValues ? (
									<Text size="xs" accentColor="base">
										{formatCompactDate(latestProcedureStepFieldValues.submitted_at)}
										&nbsp;
										<UserIcon
											userId={latestProcedureStepFieldValues.created_by ?? -1}
											size="xs"
											className="align-center"
										/>
									</Text>
								) : null}
								<IconButton variant="ghost" size="sm" accentColor="base">
									<LuIcon
										icon={expanded ? modelIconMapping.chevronDown : modelIconMapping.chevronRight}
									/>
								</IconButton>
							</div>
						</div>

						<div className="flex flex-wrap items-center gap-2">
							<UserMultiSelect
								users={users}
								values={assignees}
								onValuesChange={handleAssigneesChange}
								size="sm"
								modal
								disabled={readonly}
							>
								<Button
									onClick={stopPropagation}
									accentColor="base"
									variant="ghost"
									size="sm"
									className="px-1! w-max"
									disabled={readonly}
								>
									<LuIcon icon={modelIconMapping.assignee} />
									{assignees.length > 0 ? (
										assignees.map((user) => <UserIcon key={user.id} userId={user.id} size="xs" />)
									) : (
										<Text accentColor="base">None</Text>
									)}
								</Button>
							</UserMultiSelect>

							<UserMultiSelect
								users={users}
								values={reviewers}
								onValuesChange={handleReviewersChange}
								size="sm"
								modal
								disabled={readonly}
							>
								<Button
									onClick={stopPropagation}
									accentColor="base"
									variant="ghost"
									size="sm"
									className="px-1! w-max"
									disabled={readonly}
								>
									<LuIcon icon={modelIconMapping.reviewer} />
									{reviewers.length > 0 ? (
										reviewers.map((user) => <UserIcon key={user.id} userId={user.id} size="xs" />)
									) : (
										<Text accentColor="base">None</Text>
									)}
								</Button>
							</UserMultiSelect>

							{procedureStepFieldValuesReviews.length > 0 ? (
								<div className="flex items-center gap-2">
									<LuIcon icon={modelIconMapping.visible} color="var(--base-a11)" size={12} />
									<Text accentColor="base" size="sm">
										{procedureStepFieldValuesReviews.length}
									</Text>
								</div>
							) : null}

							{procedureStepComments.length > 0 ? (
								<div className="flex items-center gap-2">
									<LuIcon icon={modelIconMapping.comment} color="var(--base-a11)" size={12} />
									<Text accentColor="base" size="sm">
										{procedureStepComments.length}
									</Text>
								</div>
							) : null}
						</div>
					</div>
				</RadixCollapsible.Trigger>

				<RadixCollapsible.Content className="flex flex-col gap-4">
					<Separator size="full" />

					<div className="flex flex-col gap-2">
						{computedProcedureStepFields ? (
							<div className="flex flex-col gap-4">
								<FieldsRevisionDropdownSelect
									required
									disabled={isProcedureStepFieldValuesDraft}
									fieldsRevisions={procedureStepFields}
									current={currentProcedureStepFields}
									value={computedProcedureStepFields}
									onValueChange={handleProcedureStepFieldsSelect}
									size="sm"
								>
									<Button
										size="xs"
										accentColor="base"
										variant="surface"
										className="w-max"
										disabled={isProcedureStepFieldValuesDraft}
									>
										Revision {computedProcedureStepFields.revision}
										<LuIcon icon={modelIconMapping.chevronDown} />
									</Button>
								</FieldsRevisionDropdownSelect>

								<ProcedureStepFieldsSubmitter
									size="sm"
									procedureStepFields={computedProcedureStepFields}
									onSubmit={handleProcedureStepFieldsSubmit}
									values={fieldValuesWithAttachments}
									onValuesChange={handleProcedureStepFieldValuesChange}
									hideCancel={!isProcedureStepFieldValuesDraft}
									cancelText="Draft"
									cancelIcon={modelIconMapping.delete}
									onCancel={handleDeleteProcedureStepFieldValues}
									disabled={readonly}
									hideSubmit={!isProcedureStepFieldValuesDraft}
								/>
							</div>
						) : (
							<Text accentColor="base" size="sm" weight="regular">
								No fields.
							</Text>
						)}
					</div>

					<Separator size="full" />

					<div className="flex flex-col gap-2">
						{hasActivity ? (
							<ProcedureStepActivityTimeline
								procedureStepReviews={procedureStepFieldValuesReviews}
								procedureStepEvents={procedureStepEvents}
								procedureStepComments={procedureStepComments}
								procedureStepFieldValues={procedureStepFieldValues}
							/>
						) : (
							<Text accentColor="base" size="sm" weight="regular">
								No activity.
							</Text>
						)}
					</div>

					{showCommentSubmitter && (
						<>
							<Separator size="full" />
							<form className="flex flex-col gap-2" onSubmit={handleCommentSubmit}>
								<Card className="flex h-max w-full flex-col gap-2" size="sm">
									<EditorContext value={commentSubmitterEditor}>
										<EditorContent
											editor={commentSubmitterEditor}
											className="h-max w-full grow text-sm"
										/>
										<TextBubbleMenu editor={commentSubmitterEditor} />
									</EditorContext>
								</Card>
								<div className="flex h-max w-full justify-end">
									<ButtonGroup className="flex gap-2" size="sm" variant="soft" accentColor="base">
										<Button type="button" onClick={handleCommentCancel}>
											<LuIcon icon={modelIconMapping.close} />
											Cancel
										</Button>
										<Button type="submit">
											<LuIcon icon={modelIconMapping.arrowUp} />
											Submit
										</Button>
									</ButtonGroup>
								</div>
							</form>
						</>
					)}

					{showReviewSubmitter && (
						<>
							<Separator size="full" />
							<form className="flex flex-col gap-2" onSubmit={handleReviewSubmit}>
								<Card className="flex h-max w-full flex-col gap-2" size="sm">
									<EditorContext value={reviewSubmitterEditor}>
										<EditorContent
											editor={reviewSubmitterEditor}
											className="h-max w-full grow text-sm"
										/>
										<TextBubbleMenu editor={reviewSubmitterEditor} />
									</EditorContext>
								</Card>

								<ProcedureStepFieldValuesReviewTypeRadioGroup
									size="sm"
									variant="soft"
									// @ts-expect-error REASON: enum
									value={procedureStepFieldValuesReviewType}
									// @ts-expect-error REASON: enum
									onValueChange={setProcedureStepFieldValuesReviewType}
								/>

								<div className="flex h-max w-full justify-end">
									<ButtonGroup className="flex gap-2" size="sm" variant="soft" accentColor="base">
										<Button type="button" onClick={handleReviewCancel}>
											<LuIcon icon={modelIconMapping.close} />
											Cancel
										</Button>
										<Button type="submit">
											<LuIcon icon={modelIconMapping.arrowUp} />
											Submit
										</Button>
									</ButtonGroup>
								</div>
							</form>
						</>
					)}

					{!readonly && (
						<>
							<Separator size="full" />

							<ButtonGroup
								className="flex items-center justify-between gap-2"
								size="sm"
								accentColor="base"
								variant="soft"
							>
								<div className="flex items-center gap-2">
									<Button onClick={handleCommentClick}>
										<LuIcon icon={modelIconMapping.comment} />
										Comment
									</Button>

									{latestProcedureStepFieldValues ? (
										<>
											<Button onClick={handleReviewClick}>
												<LuIcon icon={modelIconMapping.visible} />
												Review
											</Button>
										</>
									) : null}
								</div>
							</ButtonGroup>
						</>
					)}
				</RadixCollapsible.Content>
			</div>
		</RadixCollapsible.Root>
	)
})
ProcedureStepItem.displayName = "ProcedureStepItem"
